import { ChangeEvent, memo, ReactNode, useEffect, useMemo, useState } from 'react';
import MaterialStaticDatePicker, { MaterialStaticDatepickerProps } from './MaterialStaticDatePicker';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    FilledTextFieldProps,
    FormControl,
    InputLabel,
    InternalStandardProps,
    MenuItem,
    OutlinedTextFieldProps,
    Select,
    StandardTextFieldProps,
    TextField,
    TextFieldProps,
    Typography,
} from '@mui/material';
import { formatLocaleDate, formatLocaleTime } from '@/utils/formatter';
import moment from 'moment-timezone';
import { getSelectEndTimeList, getSelectStartTimeList } from './selecttimeList';
import { useTranslation } from 'react-i18next';

// type ViewType<T> = T extends 'static' ?  MaterialStaticDatepickerProps : T extends 'dialog' ? {};

type TriggerProps =
    | InternalStandardProps<StandardTextFieldProps, 'onChange'>
    | InternalStandardProps<OutlinedTextFieldProps, 'onChange'>
    | InternalStandardProps<FilledTextFieldProps, 'onChange'>;

interface BasicProps {
    title?: string;
    view: 'static' | 'dialog';

    value?:
        | {
              start: string;
              end: string;
          }
        | undefined;

    onChange?: (range: { start: string; end: string }) => void;
    fullWidth?: boolean;
    handleClose?: (value: any) => void;
    isTimePicker?: boolean;
}

type StaticProps = MaterialStaticDatepickerProps &
    BasicProps & {
        view?: 'static';
        variant?: undefined;
        DatepickerProps?: undefined;

        // onSelect: MaterialStaticDatepickerProps['onSelect'],
    };

type DialogProps = TriggerProps &
    BasicProps & {
        view?: 'dialog';
        // variant?: 'outlined' | 'standard' | 'filled',
        DatepickerProps?: MaterialStaticDatepickerProps;
        // onChange?: (date?:string) => void,
    };

export type MuiDatePickerProps = DialogProps | StaticProps;

const MuiDateRangePicker = ({
    title,
    view = 'dialog',
    value,
    isTimePicker,
    DatepickerProps = { onChange: () => {} },
    onChange = () => {},
    fullWidth,
    ...props
}: MuiDatePickerProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [start, setStart] = useState<Date | null | undefined>();
    const [end, setEnd] = useState<Date | null | undefined>();
    const [startTime, setStartTime] = useState('0');
    const [endTime, setEndTime] = useState('0');
    const [currentValue, setCurrentValue] = useState<
        | {
              start: string;
              end: string;
          }
        | undefined
    >(value);

    function string2date(dateISOString?: string | undefined) {
        try {
            if (!dateISOString) return undefined;

            const t = moment(dateISOString).utc(true).format('YYYY-MM-DDT00:00:00');
            return new Date(t);
        } catch (e) {
            return undefined;
        }
    }

    function date2localDate(date: Date | null | undefined, anchor: 'start' | 'end' = 'start') {
        try {
            const offset = moment().utcOffset();
            const changed =
                anchor !== 'end'
                    ? moment(date).format('YYYY-MM-DDTHH:mm:ssZ')
                    : !!isTimePicker
                      ? moment(date).format('YYYY-MM-DDTHH:mm:ssZ')
                      : moment(date).add(1, 'days').subtract(1, 'milliseconds').format('YYYY-MM-DDTHH:mm:ss.999Z');

            // console.log('changed', changed);

            return new Date(changed).toISOString();
        } catch (e) {
            return '';
        }
    }

    function onCancel() {
        if (!value) {
            setCurrentValue(undefined);
        } else {
            setStart(string2date(value?.start));
            setEnd(string2date(value?.end));
            setCurrentValue(value);
        }
    }

    const displayValue = useMemo(() => {
        if (!start && !end) return '';
        if (!!isTimePicker) {
            const selectStartTimeList = getSelectStartTimeList(t);
            const selectEndTimeList = getSelectEndTimeList(t);
            const checkStart =
                selectStartTimeList.filter((time) => time.value === startTime)[0]?.label || t('product.am0Hour');
            const endValue =
                selectEndTimeList.filter((time) => time.value === endTime)[0]?.label || t('product.pm1159Minute');
            const startString = `${formatLocaleDate(date2localDate(start) || '')} ${checkStart}`;
            const endString = `${formatLocaleDate(date2localDate(end) || '')} ${endValue}`;
            return `${startString} - ${endString}`;
        } else {
            return `${formatLocaleDate(date2localDate(start) || '')} - ${formatLocaleDate(date2localDate(end) || '')}`;
        }
    }, [start, end]);

    useEffect(() => {
        if (!!isTimePicker) {
            if (!!value?.start) {
                const startTimeSet = new Date(value.start).getHours();
                const startMinuteSet = new Date(value.start).getMinutes();
                if (!isNaN(startTimeSet)) {
                    const timeValue = startMinuteSet >= 30 ? startTimeSet + 0.5 : startTimeSet;
                    setStartTime(String(timeValue));
                }
            } else {
                setStartTime('0');
            }
            if (!!value?.end) {
                const endTimeSet = new Date(value.end).getHours();
                const endMinuteSet = new Date(value.end).getMinutes();
                if (!isNaN(endTimeSet)) {
                    if (endMinuteSet === 59) {
                        setEndTime('24');
                    } else {
                        const timeValue = endMinuteSet >= 30 ? endTimeSet + 0.5 : endTimeSet;
                        setEndTime(String(timeValue));
                    }
                }
            } else {
                setEndTime('24');
            }
        }
    }, []);

    useEffect(() => {
        setStart(string2date(value?.start));
        setEnd(string2date(value?.end));
    }, [value]);

    useEffect(() => {
        if (!currentValue) {
            setStart(undefined);
            setEnd(undefined);
        }

        if (view !== 'static') return;
        if (!!currentValue) {
            onChange(currentValue);
        }
    }, [currentValue, view]);

    useEffect(() => {
        if (!start || !end) return;

        setCurrentValue({
            start: date2localDate(start),
            end: date2localDate(end, 'end'),
        });
    }, [start, end]);

    // function close

    return view === 'dialog' ? (
        <>
            <TextField
                {...(props as any)}
                value={displayValue}
                onFocus={(ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setIsOpen(true);
                    ev.target.blur();
                }}
                fullWidth={fullWidth}
                onSelect={undefined}
            />

            <Dialog
                open={isOpen}
                onClose={(event: any) => {
                    onCancel();
                    setIsOpen(false);
                }}
                PaperProps={{
                    sx: {
                        maxWidth: 350,
                        minWidth: 300,
                    },
                }}
                fullWidth
            >
                <MaterialStaticDatePicker
                    {...DatepickerProps}
                    title={title}
                    selected={start}
                    startDate={start}
                    endDate={!end ? start : end}
                    selectsStart={true}
                    selectsEnd={true}
                    isDateRange
                    onDayMouseEnter={(date) => {
                        // console.log('onDayMouseEnter', date)
                        if (!start && !end) return;
                    }}
                    onSelect={(date) => {
                        if (!start && !end) {
                            setStart(date);
                            setEnd(date);
                            return;
                        }

                        if (!start) {
                            setStart(date);
                            return;
                        }

                        if (!end) {
                            setEnd(end);
                            return;
                        }

                        if (date < start) {
                            setStart(date);
                            return;
                        } else if (date > end) {
                            setEnd(date);
                            return;
                        }

                        const sDiff = Math.abs(moment(date).diff(start));
                        const eDiff = Math.abs(moment(date).diff(end));

                        if (sDiff <= eDiff) {
                            setStart(date);
                        } else {
                            setEnd(date);
                        }
                    }}
                    onChange={(date) => {
                        // console.log('onChange', date)
                    }}
                />
                {!!isTimePicker && (
                    <Box className="flex flex-col py-2" sx={{ borderRadius: '20px' }}>
                        <Box className="px-4">
                            <Divider sx={{ mb: 2 }}>{t('product.timeSelection')}</Divider>
                        </Box>
                        <Box className="flex gap-2 w-full px-4">
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">{t('product.startTime')}</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label={t('product.startTime')}
                                    value={startTime}
                                    onChange={(e) => {
                                        setStartTime(e.target.value);
                                    }}
                                >
                                    {getSelectStartTimeList(t).map((item) => {
                                        return (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">{t('product.endTime')}</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label={t('product.endTime')}
                                    value={endTime}
                                    onChange={(e) => {
                                        setEndTime(e.target.value);
                                    }}
                                >
                                    {getSelectEndTimeList(t).map((item) => {
                                        return (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}

                <DialogActions>
                    <Box className="flex justify-between w-full">
                        <Button
                            variant="outlined"
                            onClick={(ev: React.MouseEvent<HTMLButtonElement>) => {
                                setCurrentValue(undefined);
                            }}
                            sx={{
                                textTransform: 'none',
                            }}
                        >
                            {t('product.reset')}
                        </Button>
                        {/*<Button onClick={ev => {*/}
                        {/*    onCancel*/}
                        {/*    setIsOpen(false);*/}
                        {/*}}>취소</Button>*/}
                        <Box className="flex gap-3">
                            <Button
                                variant="contained"
                                disabled={!currentValue}
                                sx={{
                                    textTransform: 'none',
                                }}
                                onClick={(ev: React.MouseEvent<HTMLButtonElement>) => {
                                    if (!currentValue) return;
                                    let valueData = { start: '', end: '' };
                                    if (!!isTimePicker) {
                                        const startTimeNum = Number(startTime);
                                        const startHours = Math.floor(startTimeNum);
                                        const startMinutes = (startTimeNum % 1) * 60;
                                        const timeStartValue = new Date(
                                            moment(currentValue.start)
                                                .add(startHours, 'hours')
                                                .add(startMinutes, 'minutes')
                                                .format('YYYY-MM-DDTHH:mm:ssZ'),
                                        ).toISOString();
                                        valueData.start = timeStartValue;
                                        if (endTime === '24') {
                                            const timeEndValue = new Date(
                                                moment(currentValue.end)
                                                    .add(1, 'days')
                                                    .subtract(1, 'milliseconds')
                                                    .format('YYYY-MM-DDTHH:mm:ss.999Z'),
                                            ).toISOString();
                                            valueData.end = timeEndValue;
                                        } else {
                                            const endTimeNum = Number(endTime);
                                            const endHours = Math.floor(endTimeNum);
                                            const endMinutes = (endTimeNum % 1) * 60;
                                            const timeEndValue = new Date(
                                                moment(currentValue.end)
                                                    .add(endHours, 'hours')
                                                    .add(endMinutes, 'minutes')
                                                    .format('YYYY-MM-DDTHH:mm:ssZ'),
                                            ).toISOString();
                                            valueData.end = timeEndValue;
                                        }
                                    }
                                    const result = !!isTimePicker ? valueData : currentValue;
                                    console.log('result::::', result);
                                    onChange(result);
                                    setIsOpen(false);
                                    if (props.handleClose) {
                                        props.handleClose(result);
                                    }
                                }}
                            >
                                {t('product.complete')}
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                    onCancel();
                                    setIsOpen(false);
                                }}
                                sx={{
                                    textTransform: 'none',
                                }}
                            >
                                {t('product.close')}
                            </Button>
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    ) : (
        <MaterialStaticDatePicker
            fullWidth={fullWidth}
            {...props}
            title={title}
            onSelect={(date) => {
                if (!start && !end) {
                    setStart(date);
                    setEnd(date);
                    return;
                }

                if (!start) {
                    setStart(date);
                    return;
                }

                if (!end) {
                    setEnd(end);
                    return;
                }

                if (date < start) {
                    setStart(date);
                    return;
                } else if (date > end) {
                    setEnd(date);
                    return;
                }

                const sDiff = Math.abs(moment(date).diff(start));
                const eDiff = Math.abs(moment(date).diff(end));

                if (sDiff <= eDiff) {
                    setStart(date);
                } else {
                    setEnd(date);
                }
            }}
            onChange={(date) => {
                // console.log(date)
            }}
        />
        // <div>test</div>
    );
};

export default memo(MuiDateRangePicker);
